import React, { useState } from "react";
import {
    Box,
    Typography,
    IconButton,
    Stack,
    Card,
    CardMedia,
    CardActions,
    Dialog,
    DialogContent,
    AppBar,
    Toolbar,
    Slide,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import { TransitionProps } from "@mui/material/transitions";
import { Attachment } from "../../models/Job";

interface FileListProps {
    attachments: Attachment[];
    onDelete?: (id: string) => void;
    label?: string;
    customLabel?: string | JSX.Element | React.ReactNode;
    direction?: "row" | "column";
    stackStyle?: React.CSSProperties;
}

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & { children: React.ReactElement },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export const FileList = React.memo(
    ({ attachments, onDelete, label = "File List", direction = "column", customLabel, ...props }: FileListProps) => {
        const [openImageUrl, setOpenImageUrl] = useState<string | null>(null);
        const [playedVideos, setPlayedVideos] = useState<Record<string, boolean>>({});
        const handleVideoPlay = (id: string) => {
            setPlayedVideos((prev) => ({ ...prev, [id]: true }));
        };
        const handleVideoPause = (id: string) => {
            setPlayedVideos((prev) => ({ ...prev, [id]: false }));
        };

        const handleImageClick = (url: string) => {
            setOpenImageUrl(url);
        };

        const handleClose = () => {
            setOpenImageUrl(null);
        };
        return (
            <Box p={2}>
                {customLabel ? (
                    customLabel
                ) : (
                    <Typography fontWeight="bold" mb={2}>
                        {label}
                    </Typography>
                )}
                <Stack direction={direction} spacing={4} style={props.stackStyle}>
                    {attachments.map((attachment) => (
                        <Box key={attachment.id}>
                            <Card
                                sx={{
                                    position: "relative",
                                    width: "100%",
                                    maxWidth: "20rem",
                                    borderRadius: "1rem",
                                    overflow: "hidden",
                                    mx: "auto",
                                }}
                                onClick={() => {
                                    if (attachment.media_type === "image") {
                                        handleImageClick(attachment.media_url);
                                    }
                                }}>
                                <CardMedia
                                    component={attachment.media_type === "video" ? "video" : "img"}
                                    src={attachment.media_url}
                                    sx={{
                                        width: "100%",
                                        height: "auto",
                                        aspectRatio: "1 / 1", // Make it square
                                        objectFit: "cover",
                                    }}
                                    controls={attachment.media_type === "video"}
                                    onPlay={() => handleVideoPlay(attachment.id)}
                                    onPause={() => handleVideoPause(attachment.id)}
                                />
                                {/* Show Play button overlay if it's a video and hasn't played yet */}
                                {attachment.media_type.startsWith("video") && !playedVideos[attachment.id] && (
                                    <PlayCircleOutlineIcon
                                        sx={{
                                            position: "absolute",
                                            top: "50%",
                                            left: "50%",
                                            transform: "translate(-50%, -50%)",
                                            fontSize: "4rem",
                                            color: "white",
                                            opacity: 0.8,
                                            pointerEvents: "none", // so clicks go through to video
                                        }}
                                    />
                                )}
                            </Card>
                            {onDelete ? (
                                <CardActions
                                    sx={{
                                        justifyContent: "center",
                                        flexDirection: "row",
                                        mt: 1,
                                    }}>
                                    <IconButton
                                        onClick={() => onDelete(attachment.id)}
                                        size="large"
                                        style={{ color: "black" }}>
                                        <DeleteIcon fontSize="large" />
                                    </IconButton>
                                    <Typography variant="body2" fontWeight="bold">
                                        Delete
                                    </Typography>
                                </CardActions>
                            ) : null}
                        </Box>
                    ))}
                </Stack>
                {/* Fullscreen Modal */}
                <Dialog
                    fullScreen
                    open={Boolean(openImageUrl)}
                    onClose={handleClose}
                    TransitionComponent={Transition}
                    aria-labelledby="full-screen-image-modal">
                    {/* Top AppBar */}
                    <AppBar
                        sx={{
                            position: "relative",
                            backgroundColor: "rgba(0, 0, 0, 0.85)",
                            boxShadow: "none",
                        }}>
                        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
                            <Typography
                                id="full-screen-image-modal"
                                sx={{ flex: 1 }}
                                variant="h6"
                                component="div"
                                color="inherit">
                                Image Preview
                            </Typography>
                            <IconButton
                                edge="end"
                                color="inherit"
                                onClick={handleClose}
                                aria-label="Close"
                                sx={{
                                    padding: 1.5,
                                }}>
                                <CloseIcon sx={{ fontSize: { xs: "2rem", sm: "2.5rem" } }} />
                            </IconButton>
                        </Toolbar>
                    </AppBar>

                    <DialogContent
                        sx={{
                            p: 0,
                            backgroundColor: "black",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}>
                        {openImageUrl && (
                            <Box
                                component="img"
                                src={openImageUrl}
                                alt="Uploaded full screen preview"
                                sx={{
                                    maxWidth: "100%",
                                    maxHeight: "100%",
                                    objectFit: "contain",
                                }}
                            />
                        )}
                    </DialogContent>
                </Dialog>
            </Box>
        );
    },
);

FileList.displayName = "FileList";
